'use client';

import { ChevronLeft, Info } from 'lucide-react';
import type { SupportContext } from '@/lib/schemas/support.schema';
import { describeBrowser, formatBytes, VIEW_LABELS } from '@/lib/support/labels';
import { formatAgo } from '@/lib/projects/grouping';

/** « Voir ce qui sera envoyé » (P04) : tout ce qui part avec un probleme, et la case de la copie */
export function AttachedContext({ context, copyBytes, hasCamera, attachCopy, onToggleCopy, onBack }: {
  context: SupportContext;
  /** Le poids de la copie, ou null sans projet ouvert */
  copyBytes: number | null;
  hasCamera: boolean;
  attachCopy: boolean;
  onToggleCopy: (attach: boolean) => void;
  onBack: () => void;
}) {
  const { page, project, errors = [], requests = [], trail = [], env } = context;
  const inEditor = !!project;
  const lastSave = project?.lastSaveAt ? `${formatAgo(project.lastSaveAt)} · ${project.lastSaveOk ? 'réussi' : 'en échec'}` : 'aucun depuis l\'ouverture';
  const count = (n: number, one: string, many: string) => (n === 0 ? 'aucune' : `${n} ${n > 1 ? many : one}`);
  return (
    <div className="grid min-h-0 flex-1 content-start gap-3 overflow-auto px-4 py-3">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 justify-self-start text-xs font-semibold text-[var(--accent-700)] hover:underline">
        <ChevronLeft size={14} /> Retour au message
      </button>
      <p className="m-0 text-xs leading-relaxed">Voici tout ce qui part avec votre message. Rien d&apos;autre n&apos;est envoyé : ni mot de passe, ni cookie, ni le contenu de vos autres projets.</p>
      {copyBytes === null
        ? <Notice>Aucun projet n&apos;est ouvert : pas de copie de projet.</Notice>
        : <Section title="La copie du projet" items={[
            ['Projet', `${project!.name} · ${formatBytes(copyBytes)}`],
            ['État', 'tel qu\'affiché maintenant, modifications non enregistrées comprises'],
            ['Dernier enregistrement', lastSave],
          ]} />}
      <Section title="La page" items={inEditor ? [
        ['Page', 'Éditeur'],
        ['Vue', [page?.view ? VIEW_LABELS[page.view] : null, page?.floorName ? `étage ${page.floorName}` : null].filter(Boolean).join(' · ') || '—'],
        ['Outil', [page?.tool, page?.selection ? `${page.selection.label} sélectionné(e)` : null].filter(Boolean).join(' · ') || '—'],
        ...(hasCamera ? [['Caméra', 'restituée à l\'identique'] as [string, string]] : []),
      ] : [['Page', page?.title ?? '—'], ['Adresse', page?.path ?? '—']]} />
      <Section title="Ce qui s'est passé juste avant" items={[
        ['Erreurs', errors.length ? `${errors.length} erreur${errors.length > 1 ? 's' : ''} de la page` : 'aucune'],
        ['Requêtes en échec', count(requests.length, 'requête', 'requêtes')],
        ['Vos dernières actions', trail.length ? `${trail.length} (${trail.slice(-2).reverse().map(t => t.label.toLowerCase()).join(', ')}…)` : 'aucune'],
      ]} />
      {env && <Section title="Votre ordinateur" items={[
        ['Navigateur', describeBrowser(env.userAgent)],
        ['Écran', `${env.screen ?? '—'}${env.dpr ? ` · zoom ${Math.round(env.dpr * 100)} %` : ''}`],
        ['Carte graphique', env.gpu ?? 'inconnue'],
        ['Version', `archifacil ${env.version}`],
      ]} />}
      <label className="flex gap-2.5 rounded-[10px] border border-[var(--ink-200)] p-3 text-xs">
        <input type="checkbox" checked={attachCopy && copyBytes !== null} disabled={copyBytes === null} onChange={e => onToggleCopy(e.target.checked)} className="mt-0.5" />
        <span><b>Joindre la copie du projet</b><br /><span className="text-[var(--ink-500)]">Décochez pour envoyer le reste sans le projet.</span></span>
      </label>
    </div>
  );
}

function Section({ title, items }: { title: string; items: [string, string][] }) {
  return (
    <div>
      <span className="mb-1.5 block text-[11.5px] font-bold text-[var(--ink-700)]">{title}</span>
      <dl className="m-0 grid grid-cols-[120px_1fr] gap-x-2 gap-y-1 text-xs">
        {items.map(([k, v]) => <div key={k} className="contents"><dt className="text-[var(--ink-500)]">{k}</dt><dd className="m-0 text-[var(--ink-800)]">{v}</dd></div>)}
      </dl>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 rounded-[10px] border border-[var(--accent-100)] bg-[var(--accent-50)] px-3 py-2 text-xs text-[var(--accent-700)]">
      <Info size={15} className="mt-0.5 flex-none" /><span>{children}</span>
    </div>
  );
}
